// src/store/thunks/authThunks.ts

import {setPassword, setEmail, setDerivedKey, setKeyPair, setPrivateKeyHash } from '@/store/slices/poc/pocAuthSlice';

import { createAppAsyncThunk } from '../../createAppAsyncThunk';

/**
 * Thunk to handle the authentication flow:
 * 1. Set email and password in auth state
 * 2. Calculate password hash
 * 3. Generate key pair
 * 4. Send keys to server
 */
export const authenticateUser = createAppAsyncThunk(
  'auth/authenticate',
  async(
    { email, password, onSuccess }: { email: string; password: string; onSuccess: (password: string) => void },
    { dispatch, getState, extra: { pocServices, loggerService }, }
  ) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:auth' });
    try {
        const { cryptoService, pocAuthService } = pocServices;
      // Step 1: Set email and password in auth state
      dispatch(setEmail(email));
      dispatch(setPassword(password));

      // Step 2: Calculate hash
      const hashResult = await pocServices.cryptoService.hashPassword(password, email);

      // Step 3: Generate MLKEM key pair
      const keyPair = await cryptoService.generateMlKemKeyPair();

      // Calculate SHA-256 hash of the private key
      const encryptedPrivateKey = await cryptoService.encryptPrivateKey(keyPair.privateKey,hashResult);

      // Convert privateKeyHash to hex string for storage
      // Store the key information in Redux
      dispatch(setDerivedKey(hashResult.keyHex));
      dispatch(setKeyPair(keyPair));
      dispatch(setPrivateKeyHash(encryptedPrivateKey));
      log.debug('Generated key pair', JSON.stringify(Array.from(keyPair.publicKey)), JSON.stringify({ key: encryptedPrivateKey }));
      // Step 4: Upload the keys to the server for storage

      // Only call the success callback if provided (will be empty in first step)
      if (typeof onSuccess === 'function') {
        onSuccess(password);
      }

      return {
        email,
        publicKey: Array.from(keyPair.publicKey),
        privateKeyHash: encryptedPrivateKey
      };
    } catch (error) {
      // Ensure UI is reset on error
      log.error('Authentication error:', error);
      throw error instanceof Error ? error : new Error('Unknown authentication error');
    }
  }
);
